import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Song } from 'types/song';
import { fetchGlobalSearchEffect } from 'app/store/GlobalSearch/effects';
import { ProfileCard } from 'types/profile';
import { LoadingState } from 'types/enums';
import { Link } from 'types/api.types';

export interface GlobalSearchState {
  search: string;
  results: {
    songs: Song[];
    collaborators: ProfileCard[];
  };
  searchByCollaborator?: ProfileCard;
  globalSearchLoading: LoadingState;
  _links: {
    next?: Link;
  };
}

export const globalSearchInitialState: GlobalSearchState = {
  search: '',
  results: {
    songs: [],
    collaborators: [],
  },
  searchByCollaborator: undefined,
  globalSearchLoading: LoadingState.PRISTINE,
  _links: {},
};

const slice = createSlice({
  name: 'globalSearch',
  initialState: globalSearchInitialState,
  reducers: {
    setSearchAction(state, { payload }: PayloadAction<string>) {
      state.search = payload;
    },
    setSearchByCollaboratorAction(
      state,
      { payload }: PayloadAction<ProfileCard | undefined>
    ) {
      state.searchByCollaborator = payload;
      state.results = {
        songs: [],
        collaborators: [],
      };
    },
    clearResultsAction(state) {
      state.search = '';
      state.results = {
        songs: [],
        collaborators: [],
      };
      state.searchByCollaborator = undefined;
      state.globalSearchLoading = LoadingState.PRISTINE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGlobalSearchEffect.pending, (state, { meta }) => {
        if (!meta.arg?.newSearch) {
          return;
        }
        state.globalSearchLoading = LoadingState.FETCHING;
        state.results = {
          songs: [],
          collaborators: [],
        };
        state._links.next = undefined;
      })
      .addCase(fetchGlobalSearchEffect.fulfilled, (state, { payload }) => {
        if (state.search === '' && !state.searchByCollaborator) {
          state.results = {
            songs: [],
            collaborators: [],
          };
          state.globalSearchLoading = LoadingState.PRISTINE;
          return;
        }

        const songs = [...state.results.songs, ...(payload.items?.songs || [])];
        const collaborators = [
          ...state.results.collaborators,
          ...(payload.items?.collaborators || []),
        ];

        state.results = {
          songs,
          collaborators,
        };

        state._links = { ...state._links, ...payload._links };
        state._links.next = payload._links?.next;

        state.globalSearchLoading = LoadingState.FETCHED;
      })
      .addCase(fetchGlobalSearchEffect.rejected, (state) => {
        state.results = {
          songs: [],
          collaborators: [],
        };
        state.globalSearchLoading = LoadingState.ERROR;
      });
  },
});

export const {
  clearResultsAction,
  setSearchAction,
  setSearchByCollaboratorAction,
} = slice.actions;

export const globalSearchReducer = slice.reducer;
